<script>
  import { createEventDispatcher } from "svelte";
  import PlayerManagement from "../PlayerManagement.svelte";
  import { getStatusInfo } from "../../../../utils/freebetsCore.js";
  import { CAMPAIGN_STATUS } from "../../../../utils/statusEngine.js";
  import { ICONS } from "../../../../../js/icons.js";
  import { getPragmaticDemoPlayers } from "../../../../config/demoClient.js";

  export let campaign = null;
  export let clientId = "";
  export let currency = "";
  export let assignedPlayers = [];
  export let busy = false;

  const dispatch = createEventDispatcher();
  let showPlayerSelector = false;

  $: players = Array.isArray(assignedPlayers) ? assignedPlayers : [];
  $: canModifyCampaign = [
    CAMPAIGN_STATUS.ACTIVE,
    CAMPAIGN_STATUS.SCHEDULED,
  ].includes(Number(campaign?.statusCode ?? campaign?.campaignStatus));
  $: assignedPlayerIds = players
    .map((player) => player?.playerId ?? player?.id)
    .filter((id) => id !== null && id !== undefined && String(id).trim())
    .map(String);
  $: campaignTitle =
    campaign?.displayName ||
    campaign?.name ||
    campaign?.campaignName ||
    campaign?.code ||
    "Free Spin Anakatech";
  $: demoPlayers = getPragmaticDemoPlayers(
    campaign?.client ?? campaign?.clientCode ?? "",
    clientId,
    currency,
  );

  function playerStatus(player) {
    return getStatusInfo(player?.playerStatus ?? player?.status ?? 1);
  }

  function confirmSelection(event) {
    if (busy || !canModifyCampaign) return;
    showPlayerSelector = false;
    dispatch("confirm", event.detail);
  }

  function removePlayer(player) {
    if (busy || !canModifyCampaign) return;
    dispatch("remove", { player });
  }

</script>

<div class="modal-backdrop fade show"></div>
<div class="modal d-block" tabindex="-1" role="dialog" aria-modal="true">
  <div class="modal-dialog modal-lg modal-dialog-centered fixed-modal">
    <div class="modal-content modal-fixed">
      <div class="modal-header">
        <h5 class="modal-title">{campaignTitle}</h5>
        <button
          type="button"
          class="btn-close"
          aria-label="Cerrar"
          disabled={busy}
          on:click={() => dispatch("close")}
        ></button>
      </div>

      <div class="modal-body modal-body-fixed">
        <div class="top-actions">
          <div class="small text-muted">
            Moneda: {currency || "—"} · Jugadores: {players.length}
          </div>
          {#if canModifyCampaign}
            <button
              type="button"
              class="btn btn-primary btn-sm"
              disabled={busy || !clientId}
              on:click={() => (showPlayerSelector = true)}
            >
              {@html ICONS.addCircle} Agregar jugadores
            </button>
          {/if}
        </div>

        <div class="body-scroll">
          {#if players.length === 0}
            <div class="empty">No hay jugadores en esta campaña aún.</div>
          {:else}
            <div class="table-responsive">
              <table class="table table-striped table-hover table-bordered align-middle">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Jugador ID</th>
                    <th>Jugador</th>
                    <th>Free Round</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {#each players as player, index}
                    {@const status = playerStatus(player)}
                    <tr>
                      <td>{index + 1}</td>
                      <td>{player?.playerId ?? player?.id ?? "—"}</td>
                      <td>
                        {player?.playerName ?? player?.username_origin ?? player?.username ?? player?.name ?? "—"}
                      </td>
                      <td>
                        {player?.baseBonusCode ?? "—"}
                      </td>
                      <td>
                        <span class={`badge ${status.class}`}>{status.label}</span>
                      </td>
                      <td>
                        {#if canModifyCampaign}
                          <button
                            type="button"
                            class="btn btn-outline-danger btn-sm"
                            disabled={busy}
                            on:click={() => removePlayer(player)}
                          >
                            Eliminar
                          </button>
                        {/if}
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}
        </div>
      </div>

      <div class="modal-footer sticky-footer">
        {#if busy}
          <span class="me-auto small text-muted">Agregando jugadores…</span>
        {/if}
        <button
          type="button"
          class="btn btn-secondary"
          disabled={busy}
          on:click={() => dispatch("close")}
        >Cerrar</button>
      </div>
    </div>
  </div>
</div>

{#if showPlayerSelector}
  <div class="nested-dim" aria-hidden="true"></div>
  <PlayerManagement
    {clientId}
    {currency}
    selectionMode="multiple"
    initialSelected={[]}
    idStrategy="auto"
    excludeIds={assignedPlayerIds}
    staticPlayers={demoPlayers}
    on:close={() => (showPlayerSelector = false)}
    on:confirm={confirmSelection}
  />
{/if}

<style>
  .modal {
    background: rgba(0, 0, 0, 0.3);
  }
  .fixed-modal {
    max-width: 900px;
  }
  .modal-fixed {
    height: 80vh;
    display: flex;
    flex-direction: column;
  }
  .modal-body-fixed {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .top-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.5rem;
  }
  .body-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
  }
  .table-responsive {
    overflow: visible;
  }
  .table {
    margin-bottom: 0;
  }
  .table thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #fff;
  }
  .empty {
    height: 100%;
    display: grid;
    place-items: center;
    padding: 1rem;
    border: 1px dashed #ddd;
    border-radius: 8px;
    color: #777;
  }
  .modal-footer.sticky-footer {
    flex: 0 0 auto;
    background: #fff;
  }
  .nested-dim {
    position: fixed;
    inset: 0;
    z-index: 1055;
    background: rgba(0, 0, 0, 0.25);
  }
</style>
